// ---------------------------------------------------------------------------
// KgProvider — React context for the kg lifecycle.
//
// Phase 1b Step 1b.6 (per .planning/kg-repository-interface.md v1.0 §12.5):
// this provider constructs a `HybridRepository` from `@cogo/kg-repository`
// instead of calling `ensureKgReady()` from the local `kg-client.ts` file.
// Consumers can use the new `repo` field on the context to call domain
// methods (findExamples / getDesignSystem / getActions / getEvents /
// validate). The original `{ ready, loading, initResult, error, flagEnabled }`
// shape is preserved so existing `useKg()` callers keep working.
//
// DGX consumer note: workspace inventory now reads through
// `design-system-consumer.ts` and Studio's same-origin Cogo platform boundary.
// This provider keeps the platform DS fallback for repository consumers, but
// deliberately has no Supabase-backed component fallback. Admitted UI Block
// component reads are browser-safe catalog reads in `ui-block-consumer.ts`.
//
// Non-blocking init (plan doc §2.3 Option B): the provider kicks off
// `repo.ready()` eagerly on mount but does NOT gate render on completion.
// Shell paints immediately; KG-dependent consumers suspend via `useKg()`.
//
// Config source (kg-client.ts) is unchanged — `getManifestUrl()` and
// `isKgBrowserEnabled()` continue to read env/flag from there. The Phase 1a
// `kg-client.ts` file itself (and `local-validator.ts` which uses its
// `ensureKgReady`) stays in place during this step; a follow-up PR can
// migrate those consumers to the Repository API.
// ---------------------------------------------------------------------------

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { InitResult } from '@cogo/kg-browser'
import {
  HybridRepository,
  LocalWasmRepository,
  MapBackedRuleRegistry,
  PlatformRestFallback,
} from '@cogo/kg-repository'
import { getStudioAccessToken } from '@/services/auth-provider'
import {
  getCogoPlatformBase,
  withCogoDevProxyHeaderRecord,
} from '@/services/cogo-platform-config'
import { STUDIO_SHACL_RULES } from '@/features/validation/shacl-rules'
import { getManifestUrl, getWorkerBaseUrl, isKgBrowserEnabled } from './kg-client'
import { wrapRepoWithDevtools } from './lib/repo-devtools-proxy'
import { useDevToolsStore } from '@/features/devtools/store/devtools-store'
import {
  composeSinks,
  createTelemetrySinkFromEnv,
  createSentrySinkFromEnv,
  createPostHogSinkFromEnv,
  type TelemetrySink,
} from './lib/repo-telemetry'
import { setBrainstormDeps } from '@/features/brainstorm/providers'

/**
 * Phase 2b §S7 — KG Inspector devtools instrumentation.
 *
 * When enabled (DEV or `VITE_KG_INSPECTOR=1`), we wrap the HybridRepository
 * in a pass-through Proxy that reports each method call to the devtools
 * store. Prod bundles without the flag keep the raw repo; the import is
 * tree-shakeable but the module is small either way.
 */
function isKgInspectorEnabled(): boolean {
  if (import.meta.env.DEV) return true
  return import.meta.env.VITE_KG_INSPECTOR === '1'
}

// ——— Server fallback wiring (Step 1b.6 follow-up) ———
//
const PLATFORM_BASE = getCogoPlatformBase()

/**
 * Platform repository consumers share Studio's provider-aware session. On a
 * DGX appliance this is the Cogo identity token; cloud deployments retain
 * their configured auth provider through the same public helper.
 */
async function getStudioPlatformAccessToken(): Promise<string | null> {
  return getStudioAccessToken().catch(() => null)
}

export interface KgContextValue {
  /** True once initKgCore resolved OR the feature flag is off. */
  ready: boolean
  /** True while the init Promise is in flight. */
  loading: boolean
  /** Non-null when init succeeded. */
  initResult: InitResult | null
  /** Non-null when init failed. */
  error: Error | null
  /** Feature flag snapshot — cheap read for consumers. */
  flagEnabled: boolean
  /**
   * Domain Repository. Non-null once `ready === true` AND flag is on AND
   * init succeeded. Consumers should null-guard before calling methods.
   */
  repo: HybridRepository | null
  /**
   * Ask the provider to start the KG bootstrap. Called automatically by
   * `useKg()`, so a surface that never consumes the KG never pays for it.
   */
  requestBootstrap: () => void
}

const defaultValue: KgContextValue = {
  ready: !isKgBrowserEnabled(),
  loading: false,
  initResult: null,
  error: null,
  flagEnabled: isKgBrowserEnabled(),
  repo: null,
  requestBootstrap: () => {},
}

const KgContext = createContext<KgContextValue>(defaultValue)

export interface KgProviderProps {
  children: ReactNode
}

export function KgProvider({ children }: KgProviderProps) {
  // Measured on the appliance (2026-08-06): the bootstrap fetched
  // web_bg.wasm (3.8MB) + examples-v1.ttl (2.9MB) + commerce shards on the
  // SIGN-IN screen, where nothing consumes them — 7.4MB of that page's
  // 20.5MB. The work now starts on FIRST CONSUMER DEMAND (`useKg()` asks),
  // so authenticated editor surfaces behave exactly as before while
  // consumer-less surfaces (sign-in, landing) pay nothing.
  const [bootstrapRequested, setBootstrapRequested] = useState(false)
  const requestBootstrap = useCallback(() => {
    setBootstrapRequested((previous) => previous || true)
  }, [])
  const [state, setState] = useState<KgContextValue>(() => {
    const flag = isKgBrowserEnabled()
    return {
      ready: !flag, // flag off → trivially ready; server fallback is always reachable
      // Nothing is in flight until a consumer asks.
      loading: false,
      initResult: null,
      error: null,
      flagEnabled: flag,
      repo: null,
      requestBootstrap: () => {},
    }
  })

  // One-shot Repository construction. We keep a ref so strict-mode's
  // double-effect pass doesn't re-create the Repository and doubly
  // fetch the manifest.
  const repoRef = useRef<HybridRepository | null>(null)
  // Phase 2b §4.3 — telemetry sinks (Beacon / Sentry / PostHog), each
  // gated by its own env var (`VITE_KG_TELEMETRY_ENDPOINT` /
  // `VITE_SENTRY_DSN` / `VITE_POSTHOG_KEY`). Disposed on unmount.
  const telemetryRef = useRef<TelemetrySink[]>([])

  useEffect(() => {
    if (!state.flagEnabled || !bootstrapRequested) return
    let cancelled = false
    setState((previous) => (previous.loading ? previous : { ...previous, loading: true }))

    if (!repoRef.current) {
      const local = new LocalWasmRepository({
        manifestUrl: getManifestUrl(),
        workerBaseUrl: getWorkerBaseUrl(),
        verbose: import.meta.env.DEV,
        ruleRegistry: new MapBackedRuleRegistry({
          structural: STUDIO_SHACL_RULES.structural,
          binding: STUDIO_SHACL_RULES.binding,
        }),
      })
      const dsFallback = new PlatformRestFallback({
        baseUrl: PLATFORM_BASE,
        getAuthToken: getStudioPlatformAccessToken,
        headers: withCogoDevProxyHeaderRecord(),
      })
      const raw = new HybridRepository(local, {
        dsFallback,
      })
      // Phase 2b §S7 — transparent observability wrapper. When the inspector
      // flag is off AND no telemetry endpoint/DSN/key is set,
      // `wrapRepoWithDevtools` returns `raw` unchanged (composeSinks with 0
      // sinks yields no-op).
      // Phase 2b §4.3 — telemetry sink fan-out. Same per-call record is
      // forwarded to the DevTools store AND to three independent telemetry
      // sinks (Beacon / Sentry / PostHog). Each sink is env-gated — absence
      // of its config var returns `noOpTelemetrySink` and costs nothing at
      // runtime. Prod builds without any flag pay zero runtime cost beyond
      // the raw repo.
      const sinks: Array<
        (entry: Parameters<ReturnType<typeof useDevToolsStore.getState>['pushKgCall']>[0]) => void
      > = []
      if (isKgInspectorEnabled()) {
        sinks.push((entry) => useDevToolsStore.getState().pushKgCall(entry))
      }
      const shapeShaGetter = () => {
        try {
          return repoRef.current?.versionInfo().shapeSha
        } catch {
          return undefined
        }
      }
      const beaconTelemetry = createTelemetrySinkFromEnv({
        appId: 'cogo-studio',
        shapeSha: shapeShaGetter,
      })
      const sentryTelemetry = createSentrySinkFromEnv({
        appId: 'cogo-studio',
        shapeSha: shapeShaGetter,
      })
      const posthogTelemetry = createPostHogSinkFromEnv({
        appId: 'cogo-studio',
        shapeSha: shapeShaGetter,
      })
      telemetryRef.current = [beaconTelemetry, sentryTelemetry, posthogTelemetry]
      sinks.push((entry) => beaconTelemetry.track(entry))
      sinks.push((entry) => sentryTelemetry.track(entry))
      sinks.push((entry) => posthogTelemetry.track(entry))
      const onCall = sinks.length > 0 ? composeSinks(...sinks) : undefined
      repoRef.current = wrapRepoWithDevtools(raw, onCall)
    }
    const repo = repoRef.current

    repo
      .ready()
      .then(() => {
        if (cancelled) return
        // `repo.versionInfo()` is only safe after ready; we swallow any
        // access guard failures here (should never happen) since the
        // context only exposes `initResult` as a snapshot.
        let snapshot: InitResult | null = null
        try {
          const v = repo.versionInfo()
          // InitResult has a richer shape than just versionInfo; kg-browser
          // returns it from `initKgCore`. We don't have direct access to the
          // Oxigraph init result from the Repository surface today, so we
          // synthesize a minimal compatible object. Consumers that need the
          // full InitResult should call `@cogo/kg-browser`'s `getLastCapabilities()`.
          snapshot = {
            initMs: 0,
            loadedTriples: 0,
            shapeSha: v.shapeSha,
            capabilities: {
              crossOriginIsolated:
                typeof globalThis.crossOriginIsolated === 'boolean'
                  ? globalThis.crossOriginIsolated
                  : false,
              sharedArrayBuffer: typeof SharedArrayBuffer === 'function',
              wasmSimd: false,
              wasmBulkMemory: false,
              chosenPath: 'standard',
            },
          } as InitResult
        } catch {
          snapshot = null
        }
        // Wire the Brainstorm provider singleton with the now-ready
        // Repository so subsequent `getBrainstormProvider()` returns a
        // real provider that can actually call kg.findExamples /
        // kg.getNarrative. Idempotent — StrictMode re-mount sees same kg,
        // no-op. See providers/index.ts setBrainstormDeps.
        setBrainstormDeps({ kg: repo })
        setState((prev) => ({
          ...prev,
          ready: true,
          loading: false,
          initResult: snapshot,
          error: null,
          repo,
        }))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState((prev) => ({
          ...prev,
          // Graceful degrade — flag on but init failed → still "ready"
          // from the consumer's perspective because server fallback will
          // fire. Error surfaces via kg-browser logs + Sentry/PostHog later.
          ready: true,
          loading: false,
          initResult: null,
          error: err instanceof Error ? err : new Error(String(err)),
          repo: null,
        }))
      })

    return () => {
      cancelled = true
      // Drop the Brainstorm provider's kg reference so a stale Repository
      // can't hold up shutdown; KgProvider's next mount re-wires.
      setBrainstormDeps({ kg: null })
      // Dispose all telemetry sinks on unmount — flushes buffers + cancels
      // intervals + removes pagehide listener (beacon); marks Sentry/PostHog
      // sinks as disposed to stop forwarding. Safe to call repeatedly.
      for (const sink of telemetryRef.current) {
        try {
          sink.dispose()
        } catch {
          /* one sink's dispose failure must not prevent others */
        }
      }
      telemetryRef.current = []
    }
  }, [state.flagEnabled, bootstrapRequested])

  // The callback is stable, so memoizing on `state` keeps consumer renders
  // identical to the pre-demand-gating behavior.
  const value = useMemo(
    () => ({ ...state, requestBootstrap }),
    [state, requestBootstrap],
  )

  return <KgContext.Provider value={value}>{children}</KgContext.Provider>
}

export function useKg(): KgContextValue {
  const value = useContext(KgContext)
  const { requestBootstrap } = value
  useEffect(() => {
    requestBootstrap()
  }, [requestBootstrap])
  return value
}
